import { api } from "../intreceptor/apiInterceptor";
import googleMapsService from "./GoogleMaps.service";

export interface JobsiteCreateDTO {
    jobSiteName: string;
    subsite: string;
    siteContact: string;
    siteContactEmail: string;
    sectors: string[];
    industry: string;
    fundingResource?: string;
    status: 'active' | 'inactive';
    jobCompletionStatus: string;
    markAsComp: boolean;
    generalContractor: string;
    userId: string; // Added userId
    hasPLA?: boolean;
    hasMRF?: boolean;
    projectStartDate: string; // Required field - date when construction project started
}

export interface JobsiteAddressDTO {
    address1: string;
    address2: string | null;
    city: string;
    postalCode?: string;
    province: string;
    job_id: string;
    boardArea?: string;
    coordinates: number[];
    userId: string; // Added userId
}

export interface JobsiteAddressValidationDTO {
    address1: string;
    address2: string | null;
    city: string;
    postalCode?: string;
    province: string;
    boardArea?: string;
}

export type WorksType = 'union' | 'non-union' | 'mixed';

export type UnionStatus = 'union' | 'non-union' | 'mixed';

export interface JobsiteWorkersDTO {
    labels: string[];
    noOfWorkers: string;
    worksType: WorksType;
    job_id: string;
    userId: string; // Added userId
}

export interface JobTradeDTO {
    noOfTrades: string;
    tradesType: string;
    job_id: string;
    userId: string; // Added userId
}

export interface TradeCountDTO {
    tradeId: string;
    tradeName?: string;
    journeymen: number;
    apprentices: number;
    contractorId?: string;
    entries: TradeEntry[];
    workers?: string; // Add workers field for non-UA trades
}

export interface TradeContractAssociationDTO {
    job_id: string;
    contractorId: string;
    noOfJourneymen: number;
    noOfApperntices: number;
    userId: string; // Added userId
}

export interface JobsiteTraderRequestDTO {
    userId: string;
    job_id: string;
    noOfTrades: string;
    jobTrade: Array<{
        tradesType: string;
        unionStatus: UnionStatus;
        isSignatory: boolean;
        localUnionIds: string[];
        numOfWorkers: number;
        isUACanadaTrade: boolean;
        tradeContractAssociation: Array<{
            contractorId: string;
            tradeType: string;
            noOfJourneymen: number;
            noOfApperntices: number;
        }>;
    }>;
}

export interface JobsiteFilesDTO {
    jobId: string;
    files: File[];
    userId: string; // Added userId
}

interface SubmitJobsiteDTO {
    jobsiteId: string;
    userId: string;
}

export interface RestoreJobsiteDTO {
    userId: string;
    restoreReason?: string;
}

export interface JobsiteFilters {
    status?: string[];
    contractor?: string[];
    mechanicalContractor?: string[];
    localUnion?: string[];
    province?: string[];
    organizer?: string[];
    trades?: string[];
    sector?: string[];
    workersType?: string[];
    search?: string;
    limit?: string;
    page?: string;
    sortBy?: string;
    sortOrder?: string;
}

export interface JobsiteResponse {
    data: Jobsite[];
    totalCount: number;
    page: number;
    limit: number;
}

export interface AllJobsitesResponse {
    data: Jobsite[];
    totalCount: number;
}


export interface JobsiteDetailResponse {
    jobDetails: JobsiteDetail[];
    address: JobsiteAddress[];
    workers: JobsiteWorker[];
    files: JobsiteFile[];
    trades: JobsiteTrade[];
    visits?: any;
    latestVisitId?: string;
}

export interface JobsiteAddressResponse {
    id: string;
    address1: string;
    address2: string;
    city: string;
    postalCode?: string;
    coordinates: {
        type: string;
        coordinates: number[];
    };
    createdAt: string;
    updatedAt: string;
    province: {
        id: string;
        name: string;
        createdAt: string;
        updatedAt: string;
    };
    boardArea: {
        id: string;
        name: string;
        description: null | string;
        createdAt: string;
        updatedAt: string;
    };
    job_id: {
        id: string;
        jobSiteName: string;
        subsite: string;
        siteContact: string;
        status: string;
        markAsComp: boolean;
        createdAt: string;
        updatedAt: string;
        isSubmitted: boolean;
        isDeleted: boolean;
        deletedAt: string | null;
        deletionReason: string | null;
    };
}

export interface JobsiteDetail {
    id: string;
    jobSiteName: string;
    subsite: string;
    siteContact: string;
    siteContactEmail: string;
    status: string;
    markAsComp: boolean;
    createdAt: string;
    updatedAt: string;
    projectStartDate: string;
    isSubmitted: boolean;
    isDeleted: boolean;
    comments: string | null;
    updatedBy: string | null;
    restoredBy?: { id: string; name: string };
    restoredAt?: string;
    jobDetailsSectors: Array<{ sector: LookupItem }>;
    industry: LookupItem;
    fundingResource: FundingResource;
    jobCompletionStatus: JobCompletionStatus;
    contractor?: Contractor;
    generalContractor?: Contractor;
    addresses: JobsiteAddress;
    userId: User;
    hasPLA?: boolean;
    hasMRF?: boolean;
}

interface LookupItem {
    id: string;
    name: string;
    description: string | null;
    createdAt: string;
    updatedAt: string;
}

interface FundingResource {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
}

interface JobsiteAddress {
    id: string;
    address1: string;
    address2: string;
    city: string;
    postalCode?: string;
    coordinates: {
        type: string;
        coordinates: number[];
    };
    createdAt: string;
    updatedAt: string;
    province?: LookupItem;
    boardArea?: LookupItem;
}

interface JobsiteWorker {
    id: string;
    labels: string[];
    noOfWorkers: string;
    worksType: WorksType;
    createdAt: string;
    updatedAt: string;
}

interface JobsiteFile {
    id: string;
    attachmentId: string;
    createdAt: string;
    updatedAt: string;
    attachment: {
        id: string;
        filePath: string[];
        uploadedAt: string;
        createdAt: string;
        updatedAt: string;
    };
}

interface JobsiteTrade {
    id: string;
    noOfTrades: string;
    createdAt: string;
    updatedAt: string;
}

export interface Jobsite {
    id: string;
    jobSiteName: string;
    subsite: string;
    siteContact: string;
    siteContactEmail: string;
    status: string;
    markAsComp: boolean;
    createdAt: string;
    updatedAt: string | null;
    isSubmitted: boolean;
    isDeleted: boolean;
    deletedAt: string | null;
    deletionReason: string | null;
    sector: {
        id: string;
        name: string;
    };
    industry: {
        id: string;
        name: string;
    };
    addresses: {
        id: string;
        address1: string;
        address2: string;
        city: string;
        postalCode?: string;
        coordinates: {
            type: string;
            coordinates: number[];
        };
        province: {
            id: string;
            name: string;
        };
    };
    contractor: {
        id: string;
        name: string;
        email: string;
        cellNumber: string;
        isSignatory: boolean;
        isMultiTrade: boolean;
        status: string;
        additionalNotes: string;
        isDeleted: boolean;
        contractorUnionType: string;
        address: Array<{
            id: string;
            addressType: string;
            address: string;
            city: string;
            postalCode?: string;
            coordinates: {
                type: string;
                coordinates: number[];
            };
        }>;
        contractorSectors: Array<{
            id: string;
            sector: {
                id: string;
                name: string;
            };
            isActive: boolean;
        }>;
        localUnions: Array<{
            id: string;
            code: string;
        }>;
        contractorTrades: Array<{
            id: string;
            unionType: string;
        }>;
    };
    userId: {
        id: string;
        name: string;
        email: string;
        lastLogin: string;
        lastLogout: string | null;
    };
    jobCompletionStatus: {
        id: string;
        status: string;
    };
    fundingResource: {
        id: string;
        name: string;
    };
    workers: {
        labels: string[];
        noOfWorkers: string;
    };
    jobFiles: any[];
    createdBy: {
        id: string;
        name: string;
    };
    updatedBy: string | null;
    deletedBy: string | null;
    comments: any[];
    jobTrades: any | null;
    hasPLA?: boolean;
    hasMRF?: boolean;
    aggregatedUnionStatus?: 'union' | 'non-union' | 'mixed' | null;
}

interface Contractor {
    id: string;
    name: string;
    email: string;
    createdAt: string;
    updatedAt: string;
}

interface User {
    id: string;
    name: string;
    email: string;
    mobileNumber: string;
    gender: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    refreshToken: string | null;
    lastLogin: string;
    lastLogout: string;
}


interface JobCompletionStatus {
    id: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

export interface TradeContractAssociation {
    id: string;
    noOfJourneymen: number;
    noOfApperntices: number;
    createdAt: string;
    updatedAt: string;
}

export interface TradeType {
    id: string;
    name: string;
    description: null | string;
    createdAt: string;
    updatedAt: string;
}

export interface JobsiteTradeResponse {
    id: string;
    noOfTrades: string;
    createdAt: string;
    updatedAt: string | null;
    jobTrade: Array<{
        id: string;
        unionStatus: UnionStatus;
        isSignatory: boolean;
        numOfWorkers: number;
        isUACanadaTrade: boolean;
        createdAt: string;
        updatedAt: string | null;
        tradeType: {
            id: string;
            category: 'UA' | 'NON-UA';
            name: string;
            description: string | null;
            createdAt: string;
            updatedAt: string;
        };
        localUnionIds: Array<{
            id: string;
            code: string;
            unionNumber: number;
            createdAt: string;
            updatedAt: string;
        }>;
        tradeContractAssociations: Array<{
            id: string;
            noOfJourneymen: number;
            noOfApperntices: number;
            createdAt: string;
            updatedAt: string | null;
            contractor: {
                id: string;
                name: string;
                email: string;
                cellNumber: string | null;
                isSignatory: boolean;
                isMultiTrade: boolean;
                status: string;
                additionalNotes: string | null;
                isDeleted: boolean;
                createdAt: string;
                updatedAt: string;
                deletedAt: string | null;
                contractorUnionType: string | null;
            };
            tradeType: {
                id: string;
                category: 'UA' | 'NON-UA';
                name: string;
                description: string | null;
                createdAt: string;
                updatedAt: string;
            };
        }>;
    }>;
}

export interface JobsiteUpdateDTO {
    jobSiteName: string;
    subsite: string;
    siteContact: string;
    siteContactEmail: string;
    sectors: string[];
    industry: string;
    fundingResource?: string;
    status: 'active' | 'inactive';
    jobCompletionStatus: string;
    markAsComp: boolean;
    generalContractor: string;
    userId: string;
    hasPLA?: boolean;
    hasMRF?: boolean;
    projectStartDate?: string;
}

export interface JobsiteAddressUpdateDTO {
    address1?: string;
    address2?: string | null;
    city?: string;
    postalCode?: string;
    province?: string;
    job_id?: string;            
    userId?: string;
    boardArea?: string;
    coordinates?: number[];
}

export interface JobsiteWorkersUpdateDTO {
    labels?: string[];
    noOfWorkers?: string;
    worksType?: WorksType;
    job_id?: string;
    userId?: string;
}

export interface JobTradeUpdateDTO {
    noOfTrades?: string;
    tradesType?: string;
    job_id?: string;
    userId?: string;
}

export interface TradeContractAssociationUpdateDTO {
    job_id?: string;
    contractorId?: string;
    noOfJourneymen?: number;
    noOfApperntices?: number;
}

export interface JobsiteTradesUpdateDTO {
    userId: string;
    job_id: string;
    noOfTrades: string;
    jobTrade: Array<{
        tradesType: string;
        unionStatus: UnionStatus;
        isSignatory: boolean;
        localUnionIds: string[];
        numOfWorkers: number;
        isUACanadaTrade: boolean;
        tradeContractAssociation: Array<{
            contractorId: string;
            tradeType: string;
            noOfJourneymen: number;
            noOfApperntices: number;
        }>;
    }>;
}

export interface TradeEntry {
    contractorId: string;
    journeymen: number;
    apprentices: number;
}

export interface JobsiteCountResponse {
    totalJobsites: number;
    completed: number;
    inProgress: number;
    completionRate: number;
}

export interface WorkersPercentageResponse {
    province: string;
    percentages: {
        union: number;
        nonUnion: number;
        mixed: number;
    };
    totalWorkers: number;
    counts: {
        union: number;
        nonUnion: number;
        mixed: number;
    };
}

export interface ComparisonResponse {
    sector: string;
    industries?: {
        name: string;
        union: number;
        mixed: number;
        nonUnion: number;
    }[];
    union?: number;
    mixed?: number;
    nonUnion?: number;
}

export interface FundingResourceResponse {
    totalJobsites: number;
    resources: {
        name: string;
        count: number;
        percentage: number;
    }[];
}

export interface StagesResponse {
    sector: string;
    stages: {
        started: number;
        siteServices: number;
        buildingShell: number;
        roughIn: number;
        finishWork: number;
        completed: number;
    };
}

// Utility function to geocode an address if coordinates are missing
export const geocodeAddressIfNeeded = async (addressData: any): Promise<any> => {
    try {
        // If coordinates already exist and are valid, return as is
        if (addressData.coordinates?.coordinates && addressData.coordinates.coordinates.length === 2) {
            return addressData;
        }

        // Build the full address string for geocoding
        const addressParts = [
            addressData.address1,
            addressData.city
        ].filter(part => part && part.trim() !== '');
        
        if (addressData.postalCode) {
            addressParts.push(addressData.postalCode);
        }
        
        // Add province name if available
        if (addressData.province?.name) {
            addressParts.push(addressData.province.name);
        }
        
        // Always add Canada at the end
        addressParts.push('Canada');
        
        const fullAddress = addressParts.join(', ');
        console.log('Geocoding address on frontend:', fullAddress);
        
        // Perform geocoding using Google Maps API
        const google = await googleMapsService.loadMapsApi();
        const geocoder = new google.maps.Geocoder();
        
        const coordinates = await new Promise<number[]>((resolve) => {
            geocoder.geocode({ address: fullAddress }, (results: any, status: any) => {
                const locationType = results?.[0]?.geometry?.location_type;
                const isAccurate = locationType === 'ROOFTOP' || locationType === 'RANGE_INTERPOLATED';
                if (status === 'OK' && results[0] && isAccurate) {
                    const location = results[0].geometry.location;
                    const coords = [location.lat(), location.lng()];
                    resolve(coords);
                } else {
                    console.warn('Frontend geocoding failed for address:', fullAddress, 'Status:', status, 'locationType:', locationType);
                    resolve([]);
                }
            });
        });
        
        // Update the address data with geocoded coordinates
        if (coordinates.length === 2) {
            const updatedAddressData = {
                ...addressData,
                coordinates: {
                    type: 'Point',
                    coordinates: coordinates
                }
            };
            
            // Optionally, save the geocoded coordinates back to the server
            try {
                if (addressData.id) {
                                         await JobsiteService.updateJobsiteAddress(addressData.id, {
                        address1: addressData.address1,
                        address2: addressData.address2,
                        city: addressData.city,
                        postalCode: addressData.postalCode || '',
                        province: addressData.province?.id || addressData.province,
                        job_id: addressData.job_id?.id || addressData.job_id,
                        coordinates: coordinates,
                        userId: 'system-geocode' // System-generated geocoding
                    });
                    console.log('Saved geocoded coordinates to server for address ID:', addressData.id);
                }
            } catch (saveError) {
                console.warn('Failed to save geocoded coordinates to server:', saveError);
                // Continue anyway - we have the coordinates for display
            }
            
            return updatedAddressData;
        }
        
        return addressData; // Return original if geocoding failed
    } catch (error) {
        console.error('Error geocoding address on frontend:', error);
        return addressData; // Return original data if error occurs
    }
};

export const JobsiteService = {
    async createJobsite(jobsiteData: JobsiteCreateDTO): Promise<Jobsite> {
        return api.fetch('/jobsite-management/jobsite-details', {
            method: 'POST',
            body: JSON.stringify(jobsiteData)
        });
    },

    async checkDuplicateJobsite(data: {
        jobSiteName: string;
        generalContractor: string;
        address: JobsiteAddressValidationDTO;
        excludeJobsiteId?: string;
    }): Promise<{ isDuplicate: boolean }> {
        return api.fetch('/jobsite-management/check-duplicate-jobsite', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    async createJobsiteAddress(addressData: JobsiteAddressDTO): Promise<JobsiteAddress> {
        return api.fetch('/jobsite-management/jobsite-address', {
            method: 'POST',
            body: JSON.stringify(addressData)
        });
    },
    
    async createJobsiteWorkers(workersData: JobsiteWorkersDTO): Promise<JobsiteWorker> {
        return api.fetch('/jobsite-management/jobsite-workers', {
            method: 'POST',
            body: JSON.stringify(workersData)
        });
    },
    // jobsite-management/jobsite-trades

    async createJobsiteTraders(tradersData: JobsiteTraderRequestDTO) {
        return api.fetch('/jobsite-management/jobsite-trades', {
            method: 'POST',
            body: JSON.stringify(tradersData)
        });
    },

    async createJobsiteFiles(filesData: JobsiteFilesDTO) {
        const formData = new FormData();
        formData.append('jobId', filesData.jobId);
        formData.append('userId', filesData.userId);
        
        filesData.files.forEach((file) => {
            formData.append('files', file);
        });

        try {
            return await api.fetch('/jobsite-management/jobsite-files', {
                method: 'POST',
                body: formData,
                isFormData: true
            });
        } catch (error) {
            console.error('Error uploading files:', error);
            throw error;
        }
    },

    async submitJobsite(data: SubmitJobsiteDTO) {
        return api.fetch('/jobsite-management/submit-jobsite', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    async getJobsiteById(jobSiteId: string): Promise<JobsiteDetailResponse> {
        return api.fetch(`/jobsite-management/get-jobsite/${jobSiteId}`, {
            method: 'GET'
        });
    },

    async getJobsites(filters: JobsiteFilters = {}): Promise<JobsiteResponse> {
        const params = new URLSearchParams();
        // Add array parameters 
        ['status', 'contractor', 'mechanicalContractor', 'localUnion', 'province', 'organizer', 'trades', 'sector', 'workersType'].forEach(key => {
            const value = filters[key as keyof JobsiteFilters];
            if (Array.isArray(value) && value.length > 0) {
                value.forEach((item: string) => {
                    params.append(key, item);
                });
            }
        });

        // Add search parameter
        if (filters.search) params.append('search', filters.search);

        // Add pagination parameters
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.page) params.append('page', filters.page);

        // Add sorting parameters
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

        // Replace + with %20 for spaces in the query string
        const queryString = params.toString().replace(/\+/g, '%20');
        return api.fetch(`/jobsite-management/get-jobsites?${queryString}`, {
            method: 'GET'
        });
    },

    async getAllJobsites(): Promise<AllJobsitesResponse> {
        return api.fetch(`/jobsite-management/get-all-jobsites`, {
            method: 'GET'
        });
    },
   

    async getJobsiteDetails(jobsiteId: string) {
        return api.fetch(`/jobsite-management/jobdetails/${jobsiteId}`, {
            method: 'GET'
        });
    },

    async getJobsiteAddress(jobsiteId: string): Promise<JobsiteAddressResponse> {
        return api.fetch(`/jobsite-management/address/${jobsiteId}`, {
            method: 'GET'
        });
    },

    async getJobsiteWorkers(jobsiteId: string) {
        return api.fetch(`/jobsite-management/workers/${jobsiteId}`, {
            method: 'GET'
        });
    },

    async getJobsiteFiles(jobsiteId: string) {
        return api.fetch(`/jobsite-management/files/${jobsiteId}`, {
            method: 'GET'
        });
    },

    async getJobsiteTrades(jobsiteId: string): Promise<JobsiteTradeResponse> {
        return api.fetch(`/jobsite-management/trades/${jobsiteId}`, {
            method: 'GET'
        });
    },

    async markascomplete(jobSiteId: string) {
        return api.fetch(`/jobsite-management/mark-as-complete/${jobSiteId}`, {
            method: 'POST',
            body: JSON.stringify({ markAsComp: true })
        });
    },

    async updateJobsiteDetails(jobsiteId: string, jobsiteData: JobsiteUpdateDTO): Promise<{ data: Jobsite, message: string }> {
        return api.fetch(`/jobsite-management/jobsite-details/${jobsiteId}`, {
            method: 'PATCH',
            body: JSON.stringify(jobsiteData)
        });
    },

    async updateJobsiteAddress(jobsiteId: string, addressData: JobsiteAddressUpdateDTO) {
        return api.fetch(`/jobsite-management/jobsite-address/${jobsiteId}`, {
            method: 'PATCH',
            body: JSON.stringify(addressData)
        });
    },

    async updateJobsiteWorkers(jobsiteId: string, workersData: JobsiteWorkersUpdateDTO) {
        return api.fetch(`/jobsite-management/jobsite-workers/${jobsiteId}`, {
            method: 'PATCH',
            body: JSON.stringify(workersData)
        });
    },

    async updateJobsiteTrades(jobsiteId: string, tradesData: JobsiteTradesUpdateDTO) {
        return api.fetch(`/jobsite-management/jobsite-trades/${jobsiteId}`, {
            method: 'PATCH',
            body: JSON.stringify(tradesData)
        });
    },

    async updateJobsiteFiles(jobId: string, userId: string, newFiles: File[], bypassCompletionCheck: boolean = false) {
        const formData = new FormData();
        formData.append('jobId', jobId);
        formData.append('userId', userId);
        formData.append('bypassCompletionCheck', bypassCompletionCheck.toString());

        newFiles.forEach((file) => {
            formData.append('newFiles', file);
        });

        return api.fetch('/jobsite-management/update-job-files', {
            method: 'PATCH',
            body: formData,
            isFormData: true
        });
    },

    async deleteJobsiteFile(id: string) {
        return api.fetch(`/jobsite-management/jobsite-files/${id}`, {
            method: 'DELETE'
        });
    },

    async deleteJobsite(jobSiteId: string, userId: string, reason: string): Promise<{ message: string }> {
        return api.fetch(`/jobsite-management/jobsite-details/${jobSiteId}`, {
            method: 'DELETE',
            body: JSON.stringify({ userId, reason }),
        });
    },

    async restoreJobsite(jobSiteId: string, userId: string, restoreReason?: string): Promise<{ message: string }> {
        return api.fetch(`/jobsite-management/jobsite-details/${jobSiteId}/restore`, {
            method: 'PATCH',
            body: JSON.stringify({ userId, restoreReason }),
        });
    },

    async getJobsiteCount(timeRange: string): Promise<JobsiteCountResponse> {
        return api.fetch(`/dashboard/jobsite-counts?timeRange=${encodeURIComponent(timeRange)}`, {
            method: 'GET'
        });
    },

    async getWorkersPercentage(timeRange: string): Promise<WorkersPercentageResponse> {
        return api.fetch(`/dashboard/workers-percentage-by-province?timeRange=${encodeURIComponent(timeRange)}`, {
            method: 'GET'
        });
    },

    async getComparison(timeRange: string): Promise<ComparisonResponse> {
        return api.fetch(`/dashboard/jobsite-comparison?timeRange=${encodeURIComponent(timeRange)}`, {
            method: 'GET'
        });
    },

    async fundingResources(timeRange: string): Promise<FundingResourceResponse> {
        return api.fetch(`/dashboard/funding-resources-percentage?timeRange=${encodeURIComponent(timeRange)}`, {
            method: 'GET'
        });
    },

    async getCompletionStages(timeRange: string): Promise<StagesResponse> {
        return api.fetch(`/dashboard/completion-stages?timeRange=${encodeURIComponent(timeRange)}`, {
            method: 'GET'
        });
    },

    async viewFile(attachmentId: string) {
        return api.fetch(`/jobsite-management/view-file/${attachmentId}`, {
            method: 'GET',
            responseType: 'blob'
        });
    }
};
